import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search, ChevronDown } from 'lucide-react';
import { propFirmData } from '../data/propFirmDataHomepage';
import { prefetchBestPropFirmsPage } from '../utils/prefetchBestPropFirmsPage';

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const handleSearchSelect = (firmId: string) => {
    setSearchTerm('');
    setShowDropdown(false);
    setMobileOpen(false);
    navigate(`/reviews/${firmId}`);
  };

  const handleMouseEnter = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
    }
    setShowToolsDropdown(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowToolsDropdown(false);
    }, 300); // 300ms delay before closing
    setDropdownTimeout(timeout);
  };

  const filteredFirms = Object.entries(propFirmData).filter(([_, firm]) =>
    firm.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : 'auto';
  }, [mobileOpen]);

  return (
    <header className="bg-black/80 backdrop-blur sticky top-0 z-50 border-b border-yellow-800 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between w-full">
        <Link 
          to="/" 
          className="flex items-center space-x-3 font-bold text-lg text-yellow-400 no-underline hover:text-yellow-300 transition-colors"
        >
          <img
            src="/assets/logo.svg"
            alt="PropViper"
            className="w-12 h-12 sm:w-10 sm:h-10 object-contain"
          />
          <span className="inline-block tracking-tight text-base sm:text-lg">PropViper</span>
        </Link>

        <div className="hidden md:block relative ml-4 flex-1 max-w-xs">
          <div className="flex items-center bg-white rounded-md overflow-hidden px-2 text-black">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search prop firms..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(e.target.value.length > 0);
              }}
              className="bg-transparent focus:outline-none px-2 py-1 text-sm w-full"
            />
          </div>
          
          {showDropdown && (
            <div className="absolute mt-2 w-full bg-white text-black rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
              {filteredFirms.length > 0 ? (
                filteredFirms.map(([id, firm]) => (
                  <div
                    key={id}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => handleSearchSelect(id)}
                  >
                    {firm.name}
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-gray-500 text-sm">No results found</div>
              )}
            </div>
          )}
        </div>

        <nav className="hidden md:flex space-x-6 text-sm font-semibold ml-4 text-yellow-300">
          <Link 
            to="/compare" 
            className="hover:text-yellow-400 transition no-underline"
          >
            Comparison
          </Link>
          <Link
            to="/best-prop-firms"
            className="hover:text-yellow-400 transition no-underline"
            onMouseEnter={prefetchBestPropFirmsPage}
          >
            Best Prop Firms
          </Link>
          <Link 
            to="/reviews" 
            className="hover:text-yellow-400 transition no-underline"
          >
            Reviews
          </Link>
          <div className="relative group">
            <button
              className="flex items-center gap-1 hover:text-yellow-400 transition"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              Tools <ChevronDown className="w-4 h-4" />
            </button>
            {showToolsDropdown && (
              <div 
                className="absolute left-0 mt-2 w-48 bg-[#1a1a1a] border border-yellow-800 rounded-lg shadow-lg py-2"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to="/tools/lot-calculator"
                  className="block px-4 py-2 text-sm hover:bg-yellow-400/10 hover:text-yellow-400 transition"
                  onClick={() => {
                    setShowToolsDropdown(false);
                    setMobileOpen(false);
                  }}
                >
                  Lot Calculator
                </Link>
              </div>
            )}
          </div>
        </nav>

        <button
          className="md:hidden text-yellow-400"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle Menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div
          id="mobile-menu"
          className="md:hidden bg-black text-yellow-300 px-6 pb-6 pt-4 space-y-6 text-base font-semibold transition-transform z-[100]"
        >
          <div className="w-full relative">
            <div className="flex items-center bg-white rounded-md overflow-hidden px-2 text-black">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search prop firms..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(e.target.value.length > 0);
                }}
                className="bg-transparent focus:outline-none px-2 py-2 text-sm w-full"
              />
            </div>
            {showDropdown && (
              <div className="absolute mt-2 w-full bg-white text-black rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                {filteredFirms.length > 0 ? (
                  filteredFirms.map(([id, firm]) => (
                    <div
                      key={id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onClick={() => handleSearchSelect(id)}
                    >
                      {firm.name}
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500 text-sm">No results found</div>
                )}
              </div>
            )}
          </div>

          <Link 
            to="/compare" 
            className="block rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition no-underline"
          >
            Compare Firms
          </Link>
          <Link
            to="/best-prop-firms"
            className="block rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition no-underline"
            onMouseEnter={prefetchBestPropFirmsPage}
          >
            Best Firms
          </Link>
          <Link 
            to="/reviews" 
            className="block rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition no-underline"
          >
            Read Reviews
          </Link>
          <Link 
            to="/tools/lot-calculator" 
            className="block rounded-md px-4 py-3 bg-yellow-400 text-black text-center hover:bg-yellow-300 transition no-underline"
            onClick={() => setMobileOpen(false)}
          >
            Lot Calculator
          </Link>
        </div>
      )}
    </header>
  );
}
